import { FC, useEffect, useMemo, useState } from "react";
import { Prompt, useHistory } from "react-router-dom";
import { Controller, FormProvider, useForm } from "react-hook-form";
import {
  Typography,
  FormControl,
  RadioGroup,
  FormControlLabel,
  Radio,
  Box,
  Stack,
  Button,
  Alert,
  Divider,
  Skeleton,
} from "@mui/material";
import { LocalizationProvider } from "@mui/x-date-pickers";
import { AdapterDateFns } from "@mui/x-date-pickers/AdapterDateFns";
import { isEmpty } from "lodash";
import { format, setYear } from "date-fns";
import { pl } from "date-fns/locale/pl";
import { ControlledTextField } from "@benefit-systems/hr-tools-ui";
import { standardSchemaResolver } from "@hookform/resolvers/standard-schema";
import { useCompanyId } from "src/Pages/Company/CompanyContext";
import { useGlobalModal } from "src/utils/GlobalModal/GlobalModalContext";
import __ from "src/utils/Translations";
import { LogLevel, useLogStatus } from "src/utils/newRelic/useLogStatus";
import {
  fetchAbsenceRequestApprovers,
  fetchAbsenceRequestReplacers,
} from "src/Pages/Company/Absences/api/api";
import { useFetchEmployeeCalendars } from "src/Pages/Company/Absences/api/calendars.queries";
import {
  useFetchEmployeeAbsences,
  useFetchAbsenceTypeBalance,
} from "src/Pages/Company/Absences/api/employee.queries";
import { useMutateAbsenceRequest } from "src/Pages/Company/Absences/api/absenceRequests.queries";
import useNativeConfirmBeforeUnload from "src/utils/jsHelpers/useNativeConfirmBeforeUnload";
import useTransformAbsences from "src/utils/hooks/absences/useTransformAbsences";
import getMonthRange from "src/utils/Company/Absences/getMonthRange";
import countDays from "src/utils/Company/Absences/countDays";
import generateDateRange from "src/utils/Company/Absences/generateDateRange";
import { parseHourFromTimeNumber } from "src/utils/Company/Absences/parseHourFormat";
import getBillingCyclesFromRange from "src/utils/Company/Absences/getBillingCyclesFromRange";
import prepareAbsenceRequestDefaultFormValues from "src/utils/Company/Absences/prepareAbsenceRequestDefaultFormValues";
import validateAbsenceOverlap from "src/utils/Company/Absences/validateAbsenceOverlap";
import { formatDateRangePreviewFromDates } from "src/utils/Company/Absences/formatDateRangePreview";
import Fieldset from "src/Pages/Company/Absences/Forms/Fieldset";
import YearSwitcher from "src/Pages/Company/Absences/YearSwitcher";
import AbsenceRequestDatepicker from "src/Pages/Company/Absences/AbsenceRequest/AbsenceRequestDatepicker";
import AbsenceHourPicker from "src/Pages/Company/Absences/AbsenceRequest/AbsenceHourPicker";
import BalanceStatusBadge from "src/Pages/Company/Absences/AbsenceRequest/BalanceStatusBadge";
import EmployeesAutocomplete from "src/Pages/Company/Absences/AbsenceRequest/EmployeesAutocomplete/EmployeesAutocompleteForm";
import AbsenceRequestAttachments from "src/Pages/Company/Absences/AbsenceRequest/AbsenceRequestAttachments";
import AbsenceRequestCustomFields from "src/Pages/Company/Absences/AbsenceRequest/AbsenceRequestForm/AbsenceRequestCustomFields";
import AbsenceRequestBalances from "src/Pages/Company/Absences/AbsenceRequest/AbsenceRequestForm/AbsenceRequestBalances";
import { createAbsenceRequestDynamicSchema } from "src/Pages/Company/Absences/AbsenceRequest/AbsenceRequest.schema";
import {
  AbsenceTypeCycleDay,
  AbsenceTypeCycleHour,
  ApproverType,
  IAbsenceRequest,
  IAbsenceRequestDay,
  IAbsenceRequestFormAbsenceType,
  IAbsenceRequestFormValues,
  Status,
} from "src/Pages/Company/Absences/AbsenceRequest/AbsenceRequest.models";
import {
  BillingCycleConfigurationType,
  EPatternUnit,
  PresenceType,
} from "src/Pages/Company/Absences/AbsenceType/AbsenceType.models";
import { IExcludedDay } from "src/Pages/Company/Absences/Calendars/Calendars.models";

interface IAbsenceRequestFormProps {
  absenceType: IAbsenceRequestFormAbsenceType;
  employeeId: string;
  employeeDailyWorkingHours: number;
  absenceRequestData?: IAbsenceRequest;
}

const AbsenceRequestForm: FC<IAbsenceRequestFormProps> = ({
  absenceType,
  employeeId,
  employeeDailyWorkingHours,
  absenceRequestData,
}) => {
  const history = useHistory();
  const companyId = useCompanyId();
  const logStatus = useLogStatus();
  const { setModal, resetModal } = useGlobalModal();

  const initialDate = absenceRequestData
    ? new Date(absenceRequestData?.absenceTimes[0]?.date)
    : new Date();

  const currentYear = initialDate.getFullYear().toString();

  const [displayMonths, setDisplayMonths] = useState(
    getMonthRange(initialDate)
  );
  const [hoursCount, setHoursCount] = useState(0);
  const [selectedYear, setSelectedYear] = useState(currentYear);

  const [transformAbsences] = useTransformAbsences();

  useNativeConfirmBeforeUnload();

  if (!companyId) {
    logStatus({
      level: LogLevel.DEBUG,
      message: "No company ID provided.",
      file: "AbsenceRequestForm.tsx",
    });
  }

  if (!employeeId) {
    logStatus({
      level: LogLevel.DEBUG,
      message: "No employee ID provided.",
      file: "AbsenceRequestForm.tsx",
    });
  }

  if (!absenceType) {
    logStatus({
      level: LogLevel.DEBUG,
      message: "No Absence Type data provided.",
      file: "AbsenceRequestForm.tsx",
    });
  }

  const {
    data: calendarsData,
    isFetching: isFetchingCalendars,
    isError: isErrorCalendars,
  } = useFetchEmployeeCalendars({ companyId, employeeId });

  const {
    data: absencesData,
    isFetching: isFetchingAbsences,
    isError: isErrorAbsences,
  } = useFetchEmployeeAbsences({
    employeeId,
    ...displayMonths,
    statuses: [
      Status.WAITING_FOR_APPROVAL,
      Status.ACCEPTED,
      Status.CORRECTION_REQUIRED,
    ],
  });

  const absenceRequestMutation = useMutateAbsenceRequest(
    employeeId,
    absenceRequestData?.absenceRequestId
  );

  const yearsWithCalendar = useMemo(() => {
    if (!calendarsData || isEmpty(calendarsData)) return [];

    return calendarsData
      .map((calendar) => calendar.year)
      .sort((a, b) => Number(a) - Number(b));
  }, [calendarsData]);

  const billingCycles = useMemo(() => {
    if (absenceType.billingCycleType === BillingCycleConfigurationType.Yearly) {
      return yearsWithCalendar || [];
    }

    if (
      absenceType.billingCycleType === BillingCycleConfigurationType.Monthly
    ) {
      return getBillingCyclesFromRange(displayMonths);
    }

    return [];
  }, [absenceType, yearsWithCalendar, displayMonths]);

  const { data: balanceData, isFetching: isFetchingBalances } =
    useFetchAbsenceTypeBalance(employeeId, billingCycles, absenceType.id);

  const [absenceRequestDays, daysWithoutMultipleRequest] = useMemo<
    [IAbsenceRequestDay[], Date[]]
  >(() => {
    if (!absencesData || !absenceType) return [[], []];

    return transformAbsences(
      absencesData,
      absenceType.configurationOptions.multipleRequestsPerDayAllowed,
      absenceRequestData?.absenceRequestId
    );
  }, [
    absenceRequestData?.absenceRequestId,
    absenceType,
    absencesData,
    transformAbsences,
  ]);

  const absenceRequestSchema = useMemo(
    () =>
      createAbsenceRequestDynamicSchema({
        config: absenceType.configurationOptions,
        approverRequired: !!absenceType.approvers.length,
        absences: daysWithoutMultipleRequest as Date[],
        customFieldsConfiguration: absenceType.customFieldsConfiguration,
      }),
    [
      absenceType.approvers.length,
      absenceType.configurationOptions,
      absenceType.customFieldsConfiguration,
      daysWithoutMultipleRequest,
    ]
  );

  const formMethods = useForm<IAbsenceRequestFormValues>({
    resolver: standardSchemaResolver(absenceRequestSchema),
    mode: "onChange",
    shouldUnregister: false,
    shouldFocusError: false,
    defaultValues: prepareAbsenceRequestDefaultFormValues(
      absenceType.name,
      absenceType.customFieldsConfiguration?.customFields,
      absenceRequestData
    ) as unknown as IAbsenceRequestFormValues,
  });

  const {
    control,
    handleSubmit,
    watch,
    setValue,
    clearErrors,
    formState: { isDirty },
    trigger,
  } = formMethods;

  const chosenUnit = watch("unit");
  const chosenDateRange = watch("dates");

  const excludedDays = useMemo(() => {
    if (!calendarsData || isEmpty(calendarsData)) return [];

    return calendarsData.reduce<IExcludedDay[]>(
      (acc, current) => acc.concat(current.excludedDays),
      []
    );
  }, [calendarsData]);

  const hasValidDateRange = useMemo(
    () => chosenDateRange && chosenDateRange.some((date) => date !== null),
    [chosenDateRange]
  );

  const approversAdditionalParams = useMemo(
    () => ({
      employeeId,
      absenceTypeId: absenceType.id,
    }),
    [absenceType.id, employeeId]
  );

  const replacersAdditionalParams = useMemo(() => {
    const [startDate, endDate] = chosenDateRange;

    clearErrors("replacer");

    const baseParams = {
      employeeId,
      absenceTypeId: absenceType.id,
    };

    if (startDate && endDate) {
      return {
        ...baseParams,
        from: format(startDate, "yyyy-MM-dd"),
        to: format(endDate, "yyyy-MM-dd"),
      };
    }

    if (startDate) {
      return {
        ...baseParams,
        from: format(startDate, "yyyy-MM-dd"),
        to: format(startDate, "yyyy-MM-dd"),
      };
    }

    return {
      ...baseParams,
      from: format(new Date(), "yyyy-MM-dd"),
      to: format(new Date(), "yyyy-MM-dd"),
    };
  }, [absenceType.id, chosenDateRange, clearErrors, employeeId]);

  const dayCount = useMemo(() => {
    if (chosenUnit === EPatternUnit.Day) {
      return countDays(
        chosenDateRange,
        absenceType.configurationOptions.billInCalendarDays,
        excludedDays
      );
    }

    if (chosenUnit === EPatternUnit.Hour) {
      if (!employeeDailyWorkingHours || !hoursCount) return 0;

      return Math.ceil(hoursCount / employeeDailyWorkingHours);
    }

    return 0;
  }, [
    absenceType.configurationOptions.billInCalendarDays,
    chosenDateRange,
    chosenUnit,
    employeeDailyWorkingHours,
    excludedDays,
    hoursCount,
  ]);

  useEffect(() => {
    if (chosenUnit === EPatternUnit.Day) {
      let calculatedHoursCount = 0;

      if (!employeeDailyWorkingHours) return;

      calculatedHoursCount = dayCount * employeeDailyWorkingHours;
      setHoursCount(calculatedHoursCount);
    }
  }, [chosenDateRange, chosenUnit, dayCount, employeeDailyWorkingHours]);

  useEffect(() => {
    if (absenceRequestData?.approver) {
      trigger("approver");
    }

    if (absenceRequestData?.replacer) {
      trigger("replacer");
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleYearChange = (year: string) => {
    const chosenYear = Number(year);
    const [startDate, endDate] = chosenDateRange;

    setSelectedYear(year);

    // If dates are selected, update their years
    if (startDate && endDate) {
      setValue("dates", [
        setYear(startDate, chosenYear),
        setYear(endDate, chosenYear),
      ]);
    } else if (startDate) {
      setValue("dates", [setYear(startDate, chosenYear), null]);
    } else {
      setValue("dates", [new Date(chosenYear, 0, 1), null]);
    }
  };

  const handleMonthChange = (month: Date) => {
    setDisplayMonths(getMonthRange(month));

    const newYear = month.getFullYear().toString();
    if (newYear !== selectedYear && yearsWithCalendar.includes(newYear)) {
      setSelectedYear(newYear);
    }
  };

  const onSubmit = (formData: IAbsenceRequestFormValues) => {
    let absenceTime: unknown = {};

    const {
      unit,
      date,
      startTime,
      endTime,
      dates,
      approver,
      replacer,
      justification,
      comment,
      attachments,
    } = formData;

    const hasOverlappingAbsence =
      absenceType.presenceType === PresenceType.Absence &&
      validateAbsenceOverlap(absencesData, date, dates);

    if (unit === EPatternUnit.Day) {
      absenceTime = {
        dates: generateDateRange(dates),
      };
    } else if (unit === EPatternUnit.Hour) {
      absenceTime = {
        date: format(date!, "yyyy-MM-dd"),
        startTime: parseHourFromTimeNumber(startTime),
        endTime: parseHourFromTimeNumber(endTime),
      };
    }

    const attachmentsIds = attachments.map((attachment) => attachment.id);

    const customFieldValues = Object.entries(formData)
      .filter(([key]) => key.startsWith("customField_"))
      .reduce((acc, [key, value]) => {
        const fieldId = key.replace("customField_", "");

        if (value instanceof Date) {
          acc[fieldId] = format(value, "yyyy-MM-dd");
        } else {
          acc[fieldId] = value;
        }
        return acc;
      }, {} as Record<string, unknown>);

    const payload = {
      absenceTypeId: absenceType.id,
      employeeId,
      absences: {
        ...(absenceTime as AbsenceTypeCycleDay | AbsenceTypeCycleHour),
        type: unit,
      },
      approverId: approver?.employeeId,
      replacerId: replacer?.employeeId,
      justification,
      comment,
      attachments: attachmentsIds,
      customFieldValues,
    };

    if (hasOverlappingAbsence) {
      setModal({
        open: true,
        title: __("Masz już złożony wniosek o nieobecność na ten dzień"),
        description: __("Czy mimo to chcesz złożyć wniosek?"),
        actions: [
          {
            label: __("Anuluj składanie wniosku"),
            variant: "outlined",
            callbackFn: () => {
              resetModal();
              history.goBack();
            },
          },
          {
            label: __("Złóż wniosek"),
            variant: "contained",
            callbackFn: () => {
              resetModal();
              absenceRequestMutation.mutate(payload);
            },
          },
        ],
      });
    } else {
      absenceRequestMutation.mutate(payload);
    }
  };

  return (
    <LocalizationProvider dateAdapter={AdapterDateFns} adapterLocale={pl}>
      <FormProvider {...formMethods}>
        <form noValidate onSubmit={handleSubmit(onSubmit)}>
          {absenceType.description && (
            <Fieldset legend={__("Opis")}>
              <Typography variant="body1">
                {absenceType.description || ""}
              </Typography>
            </Fieldset>
          )}
          <Fieldset
            legend={__("Termin wniosku")}
            helpText={
              absenceType.configurationOptions.billInHours
                ? __(
                    "Wniosek godzinny można składać wyłącznie na jeden dzień pracy. Jeżeli chcesz złożyć wniosek na kilka dni, wybierz wniosek dzienny."
                  )
                : undefined
            }
          >
            <Controller
              name="unit"
              control={control}
              render={({ field: { value, onChange, ref } }) => (
                <FormControl>
                  <RadioGroup
                    value={value}
                    onChange={(event) => {
                      onChange(event.target.value);
                      setHoursCount(0);
                    }}
                    ref={ref}
                    data-testid="absence-request-form-field__unit"
                  >
                    <FormControlLabel
                      value={EPatternUnit.Day}
                      control={<Radio />}
                      label={__("Dzienny")}
                    />
                    {absenceType.configurationOptions.billInHours && (
                      <FormControlLabel
                        value={EPatternUnit.Hour}
                        control={<Radio />}
                        label={__("Godzinowy")}
                      />
                    )}
                  </RadioGroup>
                </FormControl>
              )}
            />
            {absenceType && (
              <Box sx={{ bgcolor: "grey.100", padding: "1rem 2rem 1.5rem" }}>
                <Stack
                  direction="row"
                  spacing={4}
                  sx={{ alignItems: "center" }}
                >
                  {chosenUnit === EPatternUnit.Day && hasValidDateRange && (
                    <Box>
                      <Typography component="span" variant="subtitle1">
                        {__("Termin")}:{" "}
                      </Typography>
                      <Typography component="span" variant="body1">
                        {formatDateRangePreviewFromDates(chosenDateRange)}
                      </Typography>
                    </Box>
                  )}
                  <BalanceStatusBadge
                    labelUT="Wybrane"
                    days={dayCount}
                    hours={hoursCount}
                    color="primary.dark"
                  />
                </Stack>

                <Divider />
                <Stack
                  direction="row"
                  spacing={3}
                  sx={{ alignItems: "center" }}
                >
                  {isFetchingBalances ? (
                    <Skeleton width={240} height={40} />
                  ) : (
                    <AbsenceRequestBalances
                      billingCycleType={absenceType.billingCycleType}
                      balanceData={balanceData!}
                      displayMonths={displayMonths}
                    />
                  )}
                  {chosenUnit === EPatternUnit.Day && (
                    <YearSwitcher
                      years={yearsWithCalendar}
                      selectedYear={selectedYear}
                      handleYearChange={handleYearChange}
                    />
                  )}
                </Stack>

                {chosenUnit === EPatternUnit.Day && (
                  <AbsenceRequestDatepicker
                    absenceRequestDays={absenceRequestDays}
                    excludedDays={excludedDays}
                    plannedDays={absencesData?.absencePlans || []}
                    multipleRequestsPerDayAllowed={
                      absenceType.configurationOptions
                        .multipleRequestsPerDayAllowed
                    }
                    billInCalendarDays={
                      absenceType.configurationOptions.billInCalendarDays
                    }
                    submitLimitationFrom={absenceType.submitLimitationFrom}
                    submitLimitationTo={absenceType.submitLimitationTo}
                    loading={isFetchingCalendars || isFetchingAbsences}
                    yearsWithCalendar={yearsWithCalendar}
                    displayMonths={displayMonths}
                    handleMonthChange={handleMonthChange}
                  />
                )}
                {chosenUnit === EPatternUnit.Hour && (
                  <AbsenceHourPicker
                    absenceRequestDays={absenceRequestDays}
                    excludedDays={excludedDays}
                    plannedDays={absencesData?.absencePlans || []}
                    multipleRequestsPerDayAllowed={
                      absenceType.configurationOptions
                        .multipleRequestsPerDayAllowed
                    }
                    billInCalendarDays={
                      absenceType.configurationOptions.billInCalendarDays
                    }
                    submitLimitationFrom={absenceType.submitLimitationFrom}
                    submitLimitationTo={absenceType.submitLimitationTo}
                    hoursCount={hoursCount}
                    setHoursCount={setHoursCount}
                    handleMonthChange={handleMonthChange}
                  />
                )}

                {absenceType.configurationOptions.billInHours && (
                  <Alert severity="info" sx={{ mt: "1rem" }}>
                    {__(
                      "Pierwszy wniosek złożony w danym miesiącu lub roku ustala sposób składania kolejnych. Jeśli wniosek został złożony w trybie dziennym, ta forma będzie obowiązywać dla następnych wniosków w tym okresie."
                    )}
                  </Alert>
                )}
              </Box>
            )}
          </Fieldset>
          <Fieldset
            legend={__("Osoba akceptująca wniosek")}
            divider={
              !absenceType.configurationOptions.replacerConfiguration.enabled
            }
          >
            <EmployeesAutocomplete
              name="approver"
              label={__("Imię i nazwisko")}
              enabled={!!absenceType.id}
              absenceTypeId={absenceType.id}
              queryKey="approver"
              queryFn={fetchAbsenceRequestApprovers}
              additionalQueryParams={approversAdditionalParams}
              required={!!absenceType.approvers.length}
              hasDefaultValue={!!absenceRequestData?.approver}
              setFirstAsDefault={
                !absenceRequestData &&
                absenceType.approvers.includes(ApproverType.IMMEDIATE_SUPERIOR)
              }
              disabled={!absenceType.approvers.length}
            />
          </Fieldset>
          {absenceType.configurationOptions.replacerConfiguration.enabled && (
            <Fieldset legend={__("Osoba zastępująca")}>
              <EmployeesAutocomplete
                name="replacer"
                label={__("Imię i nazwisko")}
                enabled={!!absenceType.id}
                absenceTypeId={absenceType.id}
                queryKey="replacer"
                queryFn={fetchAbsenceRequestReplacers}
                additionalQueryParams={replacersAdditionalParams}
                required={
                  absenceType.configurationOptions.replacerConfiguration
                    .required
                }
                hasDefaultValue={!!absenceRequestData?.replacer}
                showInactiveLabel
              />
            </Fieldset>
          )}
          {absenceType.configurationOptions.justificationRequired && (
            <Fieldset legend={__("Niezbędne informacje:")}>
              <ControlledTextField
                name="justification"
                label={__("Uzasadnienie nieobecności")}
                multiline
                maxRows={3}
                maxLength={255}
                required
                data-testid="absence-request-form-field__justification"
              />
            </Fieldset>
          )}
          {!isEmpty(absenceType.customFieldsConfiguration?.customFields) && (
            <AbsenceRequestCustomFields
              customFields={
                absenceType.customFieldsConfiguration?.customFields || []
              }
            />
          )}
          {absenceType.configurationOptions.attachmentConfiguration.enabled && (
            <Fieldset
              legend={__("Załączniki")}
              required={
                absenceType.configurationOptions.attachmentConfiguration
                  .required
              }
            >
              <AbsenceRequestAttachments
                attachments={absenceRequestData?.attachments}
              />
            </Fieldset>
          )}
          {absenceType.configurationOptions.commentsAllowed && (
            <Fieldset legend={__("Dodaj komentarz:")} divider={false}>
              <Typography variant="body2" sx={{ mb: "0.5rem" }}>
                {__("Dodawanie komentarza jest opcjonalne.")}
              </Typography>
              <ControlledTextField
                name="comment"
                label={__("Treść komentarza")}
                multiline
                maxRows={3}
                maxLength={255}
                data-testid="absence-request-form-field__comment"
              />
            </Fieldset>
          )}
          <Stack
            direction="row"
            sx={{ justifyContent: "flex-end", mt: "2rem" }}
            spacing={2}
          >
            <Button
              variant="outlined"
              size="large"
              onClick={() => history.goBack()}
              disabled={absenceRequestMutation.isPending}
            >
              {__("Anuluj")}
            </Button>
            <Button
              type="submit"
              variant="contained"
              size="large"
              loading={absenceRequestMutation.isPending}
              loadingPosition="start"
              data-testid="absence-request-form__submit-button"
            >
              {absenceRequestData?.absenceRequestId
                ? __("Zapisz")
                : __("Złóż wniosek")}
            </Button>
          </Stack>
        </form>
        <Prompt
          when={isDirty && !absenceRequestMutation.isSuccess}
          message={__("Niezapisane zmiany zostaną utracone.")}
        />
      </FormProvider>
    </LocalizationProvider>
  );
};

export default AbsenceRequestForm;
